import type { AssetManifest, ZoneDefinition } from '@shefira/shared';
import type Phaser from 'phaser';

import type { QuestLog } from '../ui/quest-log.js';
import { drawProp, type PropImage } from './props.js';

type Landmark = ZoneDefinition['landmarks'][number];

/**
 * A dragon's mark as the player's own story has it (ADR 0067): the farm
 * burns the night they go down to the Great Scarecrow, so until then their
 * client draws what stood there, each burnt prop's twin or nothing, and the
 * place keeps its old name. Display only: a twin keeps the footprint and the
 * blocking of what it stands for, so the server's collision is one for all.
 */
export class BeforeTheFire {
  private readonly twins: {
    quest: string;
    burnt: PropImage | undefined;
    before: PropImage | undefined;
  }[] = [];

  constructor(
    scene: Phaser.Scene,
    zone: ZoneDefinition,
    manifest: AssetManifest,
    images: readonly (PropImage | undefined)[],
  ) {
    zone.props.forEach((placed, index) => {
      if (placed.before === undefined) return;
      const { quest, prop } = placed.before;
      const before =
        prop === null
          ? undefined
          : drawProp(scene, manifest, { prop, x: placed.x, y: placed.y, flip: placed.flip });
      this.twins.push({ quest, burnt: images[index], before });
    });
    // Neither until the journal comes: a veteran must not see the farm stand
    // for a moment, nor a newcomer see it burnt.
    for (const twin of this.twins) {
      twin.burnt?.setVisible(false);
      twin.before?.setVisible(false);
    }
  }

  /** Show each mark as the journal has it. */
  follow(log: QuestLog): void {
    if (!log.known) return;
    for (const twin of this.twins) {
      const burnt = hasBurnt(log, twin.quest);
      twin.burnt?.setVisible(burnt);
      twin.before?.setVisible(!burnt);
    }
  }

  destroy(): void {
    for (const twin of this.twins) twin.before?.destroy();
    this.twins.length = 0;
  }
}

/**
 * The fire comes with the deed, not the word: once the boss has fallen, the
 * quest finished but not yet turned in, the farm is already smoking.
 */
export function hasBurnt(log: QuestLog, quest: string): boolean {
  return log.isDone(quest) || log.entry(quest)?.complete === true;
}

/** A place's name as the player's story has it. */
export function storyName(landmark: Landmark, log: QuestLog | undefined): string {
  const before = landmark.before;
  if (before === undefined || log?.known !== true) return landmark.name;
  return hasBurnt(log, before.quest) ? landmark.name : before.name;
}
